import { z } from 'zod';
import { idParamSchema, listQueryBase, multiFilter, paginatedResponse, paginationQuerySchema, } from './_shared.js';
/** How a single team's re-provision converged. */
export const teamReprovisionResultOutcomeSchema = z.enum([
    'reprovisioned', // changed something (Teams created/adopted, policy/ACL/seed applied)
    'noop', // already converged — every step a read-and-no-op
    'skipped', // not eligible (e.g. zero admins to seed)
    'failed', // threw mid-converge; left as-was, retried next run
]);
export const teamReprovisionTriggerSchema = z.enum(['manual_all', 'manual_single']);
export const teamReprovisionRunStatusSchema = z.enum(['running', 'succeeded', 'failed']);
/** One re-provision run (bulk or single-team). */
export const teamReprovisionRunSchema = z.object({
    id: z.string().uuid(),
    trigger: teamReprovisionTriggerSchema,
    triggeredBy: z.string().uuid().nullable(),
    triggeredByEmail: z.string().nullable(),
    teamId: z.string().uuid().nullable(), // null for manual_all
    status: teamReprovisionRunStatusSchema,
    teamsProcessed: z.number().int().nullable(),
    teamsSucceeded: z.number().int().nullable(),
    teamsFailed: z.number().int().nullable(),
    error: z.string().nullable(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
});
/** Per-team outcome within a run (drill-down). */
export const teamReprovisionResultSchema = z.object({
    id: z.string().uuid(),
    runId: z.string().uuid(),
    teamId: z.string().uuid(),
    teamName: z.string(),
    outcome: teamReprovisionResultOutcomeSchema,
    adminsAdded: z.number().int().nullable(),
    reviewerPolicySet: z.boolean().nullable(),
    adoAdminTeamId: z.string().nullable(),
    adoReviewerTeamId: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: z.string().datetime(),
});
// POST /api/admin/teams/:id/reprovision
export const reprovisionTeamParamsSchema = idParamSchema;
export const reprovisionTeamResponseSchema = z.object({ runId: z.string().uuid() });
// POST /api/admin/teams/reprovision-all
export const reprovisionAllResponseSchema = z.object({ runId: z.string().uuid() });
// GET /api/admin/teams/reprovision-runs
export const listTeamReprovisionRunsQuerySchema = listQueryBase.extend({
    status: multiFilter(['running', 'succeeded', 'failed']),
});
export const listTeamReprovisionRunsResponseSchema = paginatedResponse(teamReprovisionRunSchema);
export const exportTeamReprovisionRunsQuerySchema = listTeamReprovisionRunsQuerySchema.omit({
    page: true,
    pageSize: true,
});
// GET /api/admin/teams/reprovision-runs/:id/results
export const teamReprovisionRunIdParamsSchema = idParamSchema;
export const listTeamReprovisionRunResultsQuerySchema = paginationQuerySchema;
export const listTeamReprovisionRunResultsResponseSchema = paginatedResponse(teamReprovisionResultSchema);
// ────────────────────────────────────────────────────────────────────
// membership sync — adopts members added directly in ADO into the
// marketplace DB (ADO-authoritative reconciliation, distinct from
// re-provision above which pushes marketplace state out to ADO).
// ────────────────────────────────────────────────────────────────────
export const membershipSyncTriggerSchema = z.enum(['cron', 'manual', 'chained']);
export const membershipSyncRunStatusSchema = z.enum(['running', 'succeeded', 'failed']);
/** One membership-sync run (bulk or single-team, live or dry-run). */
export const membershipSyncRunSchema = z.object({
    id: z.string().uuid(),
    trigger: membershipSyncTriggerSchema,
    triggeredBy: z.string().uuid().nullable(),
    teamId: z.string().uuid().nullable(), // null for a cross-team run
    dryRun: z.boolean(),
    status: membershipSyncRunStatusSchema,
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    processed: z.number().int(),
    adopted: z.number().int(),
    deactivated: z.number().int(),
    pushed: z.number().int(),
    failed: z.number().int(),
    error: z.string().nullable(),
});
// POST /api/admin/teams/membership-sync
export const syncTeamMembershipsBodySchema = z
    .object({
    teamId: z.string().uuid().optional(),
    dryRun: z.boolean().optional(),
})
    .strict();
export const syncTeamMembershipsResponseSchema = z.object({ runId: z.string().uuid() });
// GET /api/admin/teams/membership-sync-runs
export const listMembershipSyncRunsQuerySchema = listQueryBase.extend({
    status: multiFilter(['running', 'succeeded', 'failed']),
});
export const listMembershipSyncRunsResponseSchema = paginatedResponse(membershipSyncRunSchema);
//# sourceMappingURL=admin-teams.js.map